/**
 * @fileoverview Icon button with tooltip and active state support for toggleable actions.
 * Extends TooltipIconButton with color state management for active/inactive states.
 */

import { forwardRef } from 'react';
import { TooltipIconButton, type TooltipIconButtonProps } from './TooltipIconButton';

export interface ActionIconButtonProps extends Omit<TooltipIconButtonProps, 'color'> {
  /**
   * Whether the action is in active state (e.g., pinned, bookmarked)
   * @default false
   */
  active?: boolean;
  /**
   * Color to use when active
   * @default 'primary'
   */
  activeColor?: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
  /**
   * Color to use when inactive
   * @default 'default'
   */
  inactiveColor?: 'default' | 'inherit' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
}

/**
 * Icon button with tooltip that supports active/inactive color states.
 * Useful for toggleable actions like pin, bookmark, favorite, etc.
 */
export const ActionIconButton = forwardRef<HTMLButtonElement, ActionIconButtonProps>(
  function ActionIconButton(props, ref) {
    const {
      active = false,
      activeColor = 'primary',
      inactiveColor = 'default',
      ...tooltipIconButtonProps
    } = props;

    return (
      <TooltipIconButton
        ref={ref}
        color={active ? activeColor : inactiveColor}
        {...tooltipIconButtonProps}
      />
    );
  },
);
